import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { css } from '@emotion/react';
import { tokens, Link } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import hotjarLogo from '@hotjar/shared-assets/images/logo-hotjar-white.svg';
import { FeedbackPrompt } from './FeedbackPrompt';
import { DeleteButton } from './DeleteButton';
import { ShareButton } from './ShareButton';
import { Footer } from './Footer';
import { ThumbsUpDownSection } from './ThumbsUpDownSection';
import { useRecordingsApi, STATUS } from '../useRecordingsApi';
import { ShareModal } from '../ShareModal';
import { useModalStore } from '../store/modalStore';
import { ModalStateEnum } from '../types';
import { trackClick } from '../utils/tracking';

const Container = styled.div`
  cursor: initial;
  width: 450px;
  min-height: 280px;
  background-color: ${tokens.color.neutral_700};
  color: ${tokens.color.white};
  display: flex;
  flex-direction: column;
  text-align: left;
  border: 2px solid rgba(255, 255, 255, 0.01);
  box-shadow: 0px 0 120px 40px rgba(0, 0, 0, 0.4);
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: 500;
  padding: 24px 32px ${(props) => (props.hasReadOnlySectionUnder ? '25px' : '10px')};
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 16px;
  padding: 4px 0;
  margin-bottom: 28px;
`;

const RightColumn = styled.div`
  width: 226px;
  margin-left: auto;
`;

const Content = styled.div`
  margin-top: auto;
  margin-bottom: auto;
  padding: 0px 32px;
  display: flex;
`;

const HotjarLogo = styled.img`
  width: 120px;
  margin-left: 32px;
  margin-top: 10px;
`;

const NotUsingHotjarSection = styled.div`
  margin-top: 20px;
  display: flex;
`;

const NotUsingHotjarTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const NotUsingHotjarParagraph = styled.div`
  margin-top: 8px;
  font-size: 12px;
`;

const ReadOnlyTitle = styled.div`
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  opacity: 0.3;
`;

const ReadOnlyParagraph = styled.div`
  margin-top: 8px;
  font-size: 12px;
  opacity: 0.7;
`;

export const SessionEnded = ({
  isTokenShared,
  isShared,
  isReadOnly,
  onShare,
  isCancelledNextRecordingCountdown,
  onCancelNextRecording,
  onReplay,
  isAutoplaying,
  recording,
  onTimerExpired,
  hasPreviousRecording,
  onPrevious,
  hasNextRecording,
  onNext,
  onCreateFeedback,
  showFeedbackPrompt,
  siteId,
  worthWatchingAnswer,
  onWorthWatchingAnswerChange,
  isPlaying,
  play,
  pause,
}) => {
  const { t } = useTranslation();
  const [{ status }, { setWorthWatching }] = useRecordingsApi({
    siteId,
    recordingId: recording.id,
  });

  const [isShareOpen, showShareModal] = useState(false);
  const handleOnWorthWatchingAnswerChange = useCallback(
    async (newAnswer) => {
      const oldAnswer = worthWatchingAnswer;
      onWorthWatchingAnswerChange(newAnswer);
      const isSuccess = await setWorthWatching(newAnswer);
      if (!isSuccess) {
        onWorthWatchingAnswerChange(oldAnswer);
      }
    },
    [worthWatchingAnswer, setWorthWatching, onWorthWatchingAnswerChange],
  );

  const replay = useCallback(() => {
    trackClick(recording, {
      element_name: 'playback_replayed',
      state_event_was_triggered_on: 'playback.player_navigation',
      ui_position: 'clicked_link_recording_end_screen',
    });

    onReplay();
  }, [onReplay, recording]);

  const goToPreviousRecording = useCallback(() => {
    trackClick(recording, {
      element_name: 'playback_went_to_previous_recording',
      state_event_was_triggered_on: 'playback.player_navigation',
      ui_position: 'clicked_end-screen',
    });

    onPrevious();
  }, [onPrevious, recording]);
  const goToNextRecording = useCallback(() => {
    trackClick(recording, {
      element_name: 'playback_went_to_next_recording',
      state_event_was_triggered_on: 'playback.player_navigation',
      ui_position: 'clicked_end-screen',
    });

    onNext();
  }, [onNext, recording]);

  return (
    <Container onClick={(e) => e.stopPropagation()}>
      <ShareModal
        isOpen={isShareOpen}
        isShared={isShared}
        recording={recording}
        onClose={() => showShareModal(false)}
        onShare={onShare}
        uiPosition="clicked_share_in_recording_end_screen"
        startAt={false}
        isPlaying={isPlaying}
        play={play}
        pause={pause}
      />
      <Title>{t('common:player.session-ended')}</Title>
      {!isTokenShared && (
        <ThumbsUpDownSection
          answer={worthWatchingAnswer}
          onAnswerChange={handleOnWorthWatchingAnswerChange}
          isLoading={status === STATUS.LOADING}
        />
      )}
      <Content>
        {isTokenShared && (
          <NotUsingHotjarSection>
            <LeftColumn>
              <NotUsingHotjarTitle>{t('common:player.not-using-hotjar')}</NotUsingHotjarTitle>
              <NotUsingHotjarParagraph>
                <div
                  css={css`
                    opacity: 0.7;
                  `}
                >
                  {t('common:player.start-understanding-customers')}&nbsp;
                  <span>
                    <Link
                      href="https://insights.hotjar.com/register"
                      isExternal
                      kind="inline"
                      css={css`
                        color: ${tokens.color.neutral_500};
                        &:hover {
                          color: ${tokens.color.neutral_500};
                        }
                      `}
                    >
                      {t('common:player.try-for-free')}
                    </Link>
                  </span>
                </div>
              </NotUsingHotjarParagraph>
            </LeftColumn>
            <RightColumn>
              <HotjarLogo src={hotjarLogo} alt="Hotjar logo" />
            </RightColumn>
          </NotUsingHotjarSection>
        )}
        {!isTokenShared && (
          <>
            <LeftColumn>
              <ShareButton
                onClick={() => {
                  onCancelNextRecording();
                  showShareModal(true);
                }}
                isActive={isShared}
                disabled={isReadOnly}
              />
              <DeleteButton
                onClick={() => {
                  const { setActiveModal } = useModalStore.getState();

                  onCancelNextRecording();
                  setActiveModal(ModalStateEnum.DELETE_RECORDING_OPEN);
                }}
                disabled={isReadOnly}
              />
            </LeftColumn>
            {!isReadOnly && showFeedbackPrompt && (
              <FeedbackPrompt onCreateFeedback={onCreateFeedback} siteId={siteId} />
            )}
            {isReadOnly && (
              <RightColumn>
                <ReadOnlyTitle>{t('common:player.request-for-write-access-title')}</ReadOnlyTitle>
                <ReadOnlyParagraph>
                  <Trans i18nKey="common:player.request-for-write-access-explanation">
                    {
                      'Want to share or tag recordings? Ask one of your Organization admins to give you '
                    }
                    <b>write access</b>.
                  </Trans>
                </ReadOnlyParagraph>
              </RightColumn>
            )}
          </>
        )}
      </Content>
      <Footer
        onNext={goToNextRecording}
        onPrevious={goToPreviousRecording}
        hasPreviousRecording={hasPreviousRecording}
        hasNextRecording={hasNextRecording}
        timer={8}
        isCancelledNextRecordingCountdown={isCancelledNextRecordingCountdown}
        onCancelNextRecording={onCancelNextRecording}
        onReplay={replay}
        isTokenShared={isTokenShared}
        isAutoplaying={isAutoplaying}
        onTimerExpired={onTimerExpired}
      />
    </Container>
  );
};

SessionEnded.defaultProps = {
  hasPreviousRecording: false,
  hasNextRecording: false,
  onTimerExpired: () => {},
  showFeedbackPrompt: false,
  worthWatchingAnswer: null,
  recording: null,
};

SessionEnded.propTypes = {
  isTokenShared: PropTypes.bool.isRequired,
  isShared: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  onShare: PropTypes.func.isRequired,
  isCancelledNextRecordingCountdown: PropTypes.bool.isRequired,
  onCancelNextRecording: PropTypes.func.isRequired,
  onReplay: PropTypes.func.isRequired,
  isAutoplaying: PropTypes.bool.isRequired,
  recording: PropTypes.shape({ id: PropTypes.number }),
  onTimerExpired: PropTypes.func,
  hasPreviousRecording: PropTypes.bool,
  onPrevious: PropTypes.func.isRequired,
  hasNextRecording: PropTypes.bool,
  onNext: PropTypes.func.isRequired,
  onCreateFeedback: PropTypes.func.isRequired,
  showFeedbackPrompt: PropTypes.bool,
  siteId: PropTypes.number.isRequired,
  worthWatchingAnswer: PropTypes.bool, // null is a valid value for not answered
  onWorthWatchingAnswerChange: PropTypes.func.isRequired,
  isPlaying: PropTypes.bool.isRequired,
  play: PropTypes.func.isRequired,
  pause: PropTypes.func.isRequired,
};
